import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";

import {
  Shield,
  Users,
  Activity,
  AlertTriangle
} from "lucide-react";

export default function AdminDashboard() {

  const [stats, setStats] = useState({
    users: 0,
    activeToday: 0,
    failedLogins: 0,
    auditLogs: 0
  });

  const [recentLogs, setRecentLogs] = useState([]);
  const [authLogs, setAuthLogs] = useState([]);
  const [otaErrors, setOtaErrors] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {

      const [usersRes, auditRes, authRes] =
        await Promise.all([
          api.get("/users"),
          api.get("/users/audit"),
          api.get("/auth/auth-logs")
        ]);

      const otaRes = await api.get(
        "/ota/sync-errors"
      );

      setOtaErrors(
        otaRes.data.slice(0, 5)
      );

      setStats({
        users: usersRes.data.length,

        auditLogs:
          auditRes.data.length,

        failedLogins:
          authRes.data.filter(
            (l) => l.statut === "FAILED"
          ).length,

        activeToday:
          authRes.data.filter((l) => {
            const today =
              new Date().toDateString();

            return (
              new Date(
                l.created_at
              ).toDateString() === today
            );
          }).length
      });

      setRecentLogs(
        auditRes.data.slice(0, 5)
      );

      setAuthLogs(
        authRes.data.slice(0, 20)
      );

    } catch (err) {

      console.error(
        "Erreur dashboard admin",
        err
      );

    }
  };

  return (
    <div className="p-3 md:p-6 bg-gray-100 min-h-[100dvh] space-y-4 md:space-y-6">

      {/* HEADER */}
      <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
        <Shield size={28} />
        Administration système
      </h1>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">

        <Card
          title="Utilisateurs"
          value={stats.users}
          icon={<Users />}
        />

        <Card
          title="Activité"
          value={stats.activeToday}
          icon={<Activity />}
        />

        <Card
          title="Échecs"
          value={stats.failedLogins}
          icon={<AlertTriangle />}
        />

        <Card
          title="Audit"
          value={stats.auditLogs}
          icon={<Shield />}
        />

      </div>

      {/* BLOCS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">

        {/* AUDIT */}
        <div className="bg-white p-3 md:p-4 rounded-2xl shadow">

          <h2 className="font-semibold mb-3 text-base md:text-lg">
            Dernières actions
          </h2>

          <div className="max-h-[320px] overflow-y-auto">

            <ul className="space-y-2 text-sm">

              {recentLogs.map((log) => (
                <li
                  key={log.id}
                  className="border-b pb-2"
                >
                  <strong>
                    {log.nom}
                  </strong>{" "}
                  - {log.action}

                  <div className="text-gray-500 text-xs mt-1">
                    {new Date(
                      log.created_at
                    ).toLocaleString()}
                  </div>

                </li>
              ))}

            </ul>

          </div>

          <button
            onClick={() =>
              navigate("/admin/audit")
            }
            className="mt-3 text-blue-600 text-sm hover:underline"
          >
            Voir plus →
          </button>

        </div>

        {/* CONNEXIONS */}
        <div className="bg-white p-3 md:p-4 rounded-2xl shadow">

          <div className="flex justify-between items-center mb-3">

            <h2 className="font-semibold text-base md:text-lg">
              Connexions récentes
            </h2>

            <button
              onClick={() =>
                navigate("/admin/security")
              }
              className="text-blue-600 text-sm hover:underline"
            >
              Voir plus →
            </button>

          </div>

          <div className="max-h-[320px] overflow-y-auto">

            <ul className="space-y-2 text-sm">

              {authLogs.map((log) => (
                <li
                  key={log.id}
                  className="border-b pb-2"
                >
                  <div className="break-all">
                    {log.email}
                  </div>

                  <span
                    className={
                      log.statut ===
                      "SUCCESS"
                        ? "text-green-600"
                        : "text-red-600"
                    }
                  >
                    {log.statut}
                  </span>

                  <div className="text-gray-500 text-xs mt-1">
                    {new Date(
                      log.created_at
                    ).toLocaleString()}
                  </div>

                </li>
              ))}

            </ul>

          </div>

        </div>

      </div>

      {/* OTA */}
      <div className="bg-white p-3 md:p-4 rounded-2xl shadow">

        <div className="flex justify-between items-center mb-3">

          <h2 className="font-semibold text-red-600 text-base md:text-lg">
            Erreurs OTA
          </h2>

          <button
            onClick={() =>
              navigate("/admin/ota-sync")
            }
            className="text-blue-600 text-sm hover:underline"
          >
            Voir tout →
          </button>

        </div>

        <div className="max-h-[320px] overflow-y-auto">

          <ul className="space-y-2 text-sm">

            {otaErrors.length === 0 && (
              <li className="text-gray-500">
                Aucune erreur 🎉
              </li>
            )}

            {otaErrors.map((err) => (
              <li
                key={err.id}
                className="border-b pb-2"
              >
                <div className="font-medium">
                  {err.type.toUpperCase()}
                </div>

                <div className="text-gray-600 text-xs break-all mt-1">
                  {JSON.stringify(
                    err.payload
                  )}
                </div>

                <div className="text-gray-400 text-xs mt-1">
                  {new Date(
                    err.created_at
                  ).toLocaleString()}
                </div>

              </li>
            ))}

          </ul>

        </div>

      </div>

    </div>
  );
}

function Card({
  title,
  value,
  icon
}) {

  return (
    <div className="bg-white p-3 md:p-4 rounded-2xl shadow flex items-center justify-between">

      <div>

        <div className="text-xs md:text-sm text-gray-500">
          {title}
        </div>

        <div className="text-lg md:text-xl font-bold">
          {value}
        </div>

      </div>

      <div className="text-gray-600 scale-90 md:scale-100">
        {icon}
      </div>

    </div>
  );
}